import { MoreHorizontal } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type ReactElement } from 'react';

import {
  focusMenuItem,
  menuButtonStart,
  onMenuKeyDown,
  type MenuStart,
} from '@/components/ui/menu-keys';
import { useDismiss } from '@/components/ui/use-dismiss';

// "Más acciones": las acciones secundarias de una pantalla en un menú, para el encabezado
// en móvil. Mismo patrón de menú que Exportar y la cuenta (WAI-ARIA menu button).

export interface MenuAction {
  label: string;
  onSelect: () => void;
}

export function ActionsMenu({
  items,
  label = 'Más acciones',
}: {
  items: readonly MenuAction[];
  label?: string;
}): ReactElement {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<MenuStart>('first');

  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, [wrapRef], buttonRef);

  useEffect(() => {
    if (open) {
      focusMenuItem(menuRef.current, startRef.current);
    }
  }, [open]);

  return (
    <div className="actions-menu" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="btn mut"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          startRef.current = 'first';
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          const start = menuButtonStart(e);
          if (start !== null) {
            startRef.current = start;
            if (open) {
              focusMenuItem(menuRef.current, start);
            } else {
              setOpen(true);
            }
          }
        }}
      >
        <MoreHorizontal aria-hidden="true" strokeWidth={1.9} />
        {label}
      </button>
      {open && (
        <div
          id={menuId}
          ref={menuRef}
          className="pop-menu"
          role="menu"
          tabIndex={-1}
          aria-label={label}
          onKeyDown={(e) => onMenuKeyDown(e, close)}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className="pop-item"
              onClick={() => {
                buttonRef.current?.focus();
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
